import React, { useEffect, useState } from "react";
import Modal from "../Modal/Modal";
import TripService from "../../services/TripService";
import TransporterService from "../../services/TransporterService";
import { showToast } from "../Toaster";
import { transformTruckNumber } from "../../utils/general";
import {
  ModalSection,
  ModalInfoGrid,
  ModalInfoItem,
  ModalFooter,
  ModalButton,
} from "../Modal/ModalLayout";
import truckIcon from "../../assets/icons/truck.svg";

interface TransferTripModalProps {
  isOpen: boolean;
  onClose: () => void;
  currentDriver: {
    id: number;
    name: string;
    truck: string;
  };
  tripId: number;
  transporterId?: number;
  transporterName?: string;
  jobCode?: string;
  onTransferSuccess?: () => void;
}

interface DriverRow {
  id: number;
  name: string;
  truck: string;
  transporter: string;
}

const TransferTripModal: React.FC<TransferTripModalProps> = ({
  isOpen,
  onClose,
  currentDriver,
  tripId,
  transporterId,
  transporterName,
  jobCode,
  onTransferSuccess,
}) => {
  const [drivers, setDrivers] = useState<DriverRow[]>([]);
  const [selectedDriverId, setSelectedDriverId] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  // Filter drivers based on search term
  const filteredDrivers = drivers.filter((driver) => {
    if (!searchTerm.trim()) return true;

    const searchLower = searchTerm.toLowerCase();
    const driverNameLower = driver.name.toLowerCase();
    const truckNumberLower = driver.truck.toLowerCase();
    const transporterNameLower = driver.transporter.toLowerCase();

    return (
      driverNameLower.includes(searchLower) ||
      truckNumberLower.includes(searchLower) ||
      transporterNameLower.includes(searchLower)
    );
  });

  useEffect(() => {
    if (isOpen) {
      fetchDrivers();
      setSelectedDriverId(null);
      setSearchTerm("");
    }
    // eslint-disable-next-line
  }, [isOpen, transporterId]);

  const fetchDrivers = async () => {
    setLoading(true);
    try {
      const response = await TransporterService.getAllDrivers();

      if (response?.data?.data) {
        const allDrivers = response.data.data;

        const filteredByCurrentDriver = allDrivers.filter((d: any) => {
          // If current driver ID is undefined, don't filter out any drivers
          if (!currentDriver.id) return true;
          const driverId = d.driver?.id || d.id;
          return driverId !== currentDriver.id;
        });

        const filteredByTransporter = filteredByCurrentDriver.filter(
          (d: any) => {
            if (!transporterId) return true;
            const driverTransporterId = d.transporter?.id;
            // Convert both to numbers for comparison to handle string/number mismatches
            const driverId = Number(driverTransporterId);
            const filterId = Number(transporterId);
            return driverId === filterId;
          }
        );

        // If no drivers found with transporter filter, show all drivers as fallback
        // const finalDrivers =
        //   filteredByTransporter.length > 0
        //     ? filteredByTransporter
        //     : filteredByCurrentDriver;

        const rows = filteredByCurrentDriver.map((d: any) => ({
          id: d.driver?.id || d.id,
          name: d.driver?.name || d.name,
          truck: d.currentTruck?.name || "N/A",
          transporter: d.transporter?.name || "N/A",
        }));
        setDrivers(rows);
      }
    } catch (e) {
      showToast("Failed to fetch drivers", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleTransfer = async () => {
    if (!selectedDriverId) {
      showToast("Please select a driver to transfer the trip to", "error");
      return;
    }

    try {
      const response = await TripService.transferTrip([
        {
          driverId: currentDriver.id,
          transferDriverId: selectedDriverId,
        },
      ]);

      if (response.data.error) {
        showToast(response.data.message || "Failed to transfer trip", "error");
      } else {
        showToast("Trip transferred successfully", "success");
        onTransferSuccess?.();
        onClose();
      }
    } catch (error) {
      console.error("Error transferring trip:", error);
      showToast("Failed to transfer trip", "error");
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      // title={`Transfer Trip (${tripId})${
      //   transporterName ? ` - ${transporterName}` : ""
      // }`}
      title="Transfer Trip"
      className="component_modal_container transfer_trip_modal !max-w-[650px]"
      size="xl"
      footer={
        <ModalFooter>
          <ModalButton variant="secondary" onClick={onClose} disabled={loading}>
            Cancel
          </ModalButton>
          <ModalButton
            variant="primary"
            onClick={handleTransfer}
            disabled={loading || !selectedDriverId}
          >
            Transfer Trip
          </ModalButton>
        </ModalFooter>
      }
    >
      <div className="space-y-6">
        {/* Current Trip Information */}
        {/* <div className="bg-white rounded-lg p-5 border border-[#cbd2d9] shadow-sm">
          <h3 className="text-sm font-medium text-[#37372d] mb-4 flex items-center">
            Job Information
          </h3>
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-[#f5f7f7] rounded-lg p-3">
              <p className="text-xs text-[#838285] mb-1 font-medium">
                Job Code
              </p>
              <p className="text-sm font-semibold text-[#59585b]">
                {jobCode || "N/A"}
              </p>
            </div>
            <div className="bg-[#f5f7f7] rounded-lg p-3">
              <p className="text-xs text-[#838285] mb-1 font-medium">Trip ID</p>
              <p className="text-sm font-semibold text-[#59585b]">{tripId}</p>
            </div>
          </div>
        </div> */}

        {/* Current Driver Information */}
        <div className="bg-white rounded-lg p-5 border border-[#cbd2d9] shadow-sm">
          <h3 className="text-sm font-medium text-[#37372d] mb-4 flex items-center">
            Driver Information
          </h3>
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-[#f5f7f7] rounded-lg p-3">
              <p className="text-xs text-[#838285] mb-1 font-medium">
                Driver Name
              </p>
              <p className="text-sm font-semibold text-[#59585b]">
                {currentDriver.name}
              </p>
            </div>

            <div className="bg-[#f5f7f7] rounded-lg p-3">
              <p className="text-xs text-[#838285] mb-1 font-medium">
                Current Truck Number
              </p>
              <p className="text-sm font-semibold text-[#59585b]">
                {transformTruckNumber(currentDriver.truck) || "N/A"}
              </p>
            </div>
          </div>
          <div className="mt-4">
            <div className="bg-[#f5f7f7] rounded-lg p-3">
              <p className="text-xs text-[#838285] mb-1 font-medium">
                Transporter Name
              </p>
              <p className="text-sm font-semibold text-[#59585b]">
                {transporterName || "N/A"}
              </p>
            </div>
          </div>
        </div>

        {/* Available Drivers Section */}
        <div className="bg-white rounded-lg p-5 border border-[#cbd2d9] shadow-sm">
          <h3 className="text-sm font-medium text-[#37372d] mb-4 flex items-center">
            Search Available Drivers
            {/* {transporterName && (
              <span className="text-xs text-[#6b7280] ml-2 font-normal">
                ({transporterName})
              </span>
            )} */}
          </h3>

          {/* Search Input */}
          <div className="mb-4">
            <div className="relative">
              <input
                type="text"
                placeholder="Search by driver name or truck number..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full px-4 py-3 pl-10 border border-[#cbd2d9] rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#19b5fe] focus:border-transparent transition-all duration-200"
              />
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <svg
                  className="h-4 w-4 text-[#6b7280]"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                  />
                </svg>
              </div>
            </div>
          </div>

          {loading ? (
            <div className="flex justify-center items-center py-12">
              <div className="flex items-center space-x-2">
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-[#19b5fe]"></div>
                <div className="text-sm text-[#6b7280]">Loading drivers...</div>
              </div>
            </div>
          ) : filteredDrivers.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12">
              <svg
                className="w-12 h-12 text-[#cbd2d9] mb-3"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1}
                  d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                />
              </svg>
              <div className="text-sm text-[#6b7280] text-center">
                {searchTerm.trim()
                  ? `No drivers found matching "${searchTerm}"`
                  : "No available drivers found"}
              </div>
            </div>
          ) : (
            <>
              <div className="text-xs text-[#6b7280] mb-4 flex items-center">
                <svg
                  className="w-4 h-4 mr-1"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                Found {filteredDrivers.length} available driver
                {filteredDrivers.length !== 1 ? "s" : ""}
                {searchTerm.trim() && ` matching "${searchTerm}"`}
              </div>
              <div className="space-y-3 max-h-96 overflow-y-auto">
                {filteredDrivers.map((driver) => (
                  <button
                    key={driver.id}
                    type="button"
                    onClick={() => setSelectedDriverId(driver.id)}
                    className={`w-full p-4 rounded-lg border-2 transition-all duration-200 text-left hover:shadow-sm ${
                      selectedDriverId === driver.id
                        ? "border-[#19b5fe] bg-[#19b5fe] bg-opacity-5 text-[#19b5fe] shadow-sm"
                        : "border-[#cbd2d9] bg-white text-[#59585b] hover:border-[#19b5fe] hover:border-opacity-50"
                    }`}
                  >
                    <div className="flex items-center space-x-3">
                      <div
                        className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                          selectedDriverId === driver.id
                            ? "border-[#19b5fe] bg-[#19b5fe]"
                            : "border-[#cbd2d9]"
                        }`}
                      >
                        {selectedDriverId === driver.id && (
                          <div className="w-2.5 h-2.5 bg-white rounded-full"></div>
                        )}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center justify-between mb-1">
                          <span className="font-semibold text-sm">
                            {driver.name}
                          </span>
                          <span className="text-xs text-[#838285] bg-[#f5f7f7] px-2 py-1 rounded">
                            {driver.transporter}
                          </span>
                        </div>
                        <div className="text-sm text-[#59585b] flex items-center">
                          <img
                            src={truckIcon}
                            alt="Truck"
                            className="w-4 h-4 mr-1 text-[#6b7280]"
                          />
                          Truck: {transformTruckNumber(driver.truck)}
                        </div>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </>
          )}
        </div>

        {/* Transfer Confirmation */}
        {selectedDriverId && (
          <div className="bg-[#19b5fe] bg-opacity-5 border border-[#19b5fe] border-opacity-30 rounded-lg p-4">
            <div className="flex items-center">
              <svg
                className="w-5 h-5 text-[#19b5fe] mr-3"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <div>
                <p className="text-sm text-[#19b5fe] font-medium">
                  Ready to transfer trip
                </p>
                <p className="text-xs text-[#19b5fe] text-opacity-80 mt-1">
                  Trip will be transferred to the selected driver
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
};

export default TransferTripModal;
